import React, { useState, useEffect } from 'react';
import { Bell, DollarSign, Percent, User } from 'lucide-react';
import Layout from '../components/Layout';
import { CURRENCY_OPTIONS, setDefaultCurrency, setTaxRate } from '../utils/currency';
import { api, companyProfileAPI } from '../api/client';
import { useToast } from '../components/ToastProvider';

const Settings = () => {
  const [settings, setSettings] = useState({
    currency: 'USD',
    taxRate: 0,
    companyName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { showToast } = useToast();
  const [notificationPrefs, setNotificationPrefs] = useState({
    in_app_enabled: true,
    email_enabled: false,
    digest_frequency: 'daily',
    mute_low_priority: false,
    invoice_enabled: true,
    project_enabled: true,
    expense_enabled: true,
    security_enabled: true,
    system_enabled: true,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsResponse, companyResponse] = await Promise.all([
        api.get('/settings'),
        companyProfileAPI.get(),
      ]);
      const notificationResponse = await api.get('/notifications/preferences');
      setSettings((prev) => ({
        ...prev,
        ...settingsResponse.data,
        ...companyResponse.data,
      }));
      setNotificationPrefs((prev) => ({
        ...prev,
        ...notificationResponse.data,
      }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings({ ...settings, [name]: value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await Promise.all([
        api.put('/settings', {
          currency: settings.currency,
          taxRate: settings.taxRate,
        }),
        companyProfileAPI.update({
          companyName: settings.companyName,
          email: settings.email,
          phone: settings.phone,
          address: settings.address,
          city: settings.city,
          state: settings.state,
          zipCode: settings.zipCode,
          country: settings.country,
        }),
        api.put('/notifications/preferences', notificationPrefs),
      ]);
      
      // Update localStorage for immediate UI sync
      setDefaultCurrency(settings.currency);
      setTaxRate(settings.taxRate);
      
      setMessage('Settings updated successfully!');
      showToast({
        title: 'Settings saved',
        message: 'Your settings and notification preferences were updated successfully.',
        tone: 'success',
      });
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error updating settings');
      showToast({
        title: 'Save failed',
        message: 'The settings update could not be completed. Please try again.',
        tone: 'error',
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationToggle = (key) => {
    setNotificationPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Layout pageTitle="Settings" pageSubtitle="Manage your account and application preferences">
      <div className="max-w-4xl mx-auto">

        {message && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
            {message}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-slate-200">
          <div className="px-6 py-5 border-b border-slate-200 bg-slate-50">
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Settings Sections</p>
            <div className="flex flex-wrap gap-2">
              <a href="#currency" className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:text-blue-700 hover:border-blue-200">
                <DollarSign size={16} /> Currency
              </a>
              <a href="#tax" className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:text-blue-700 hover:border-blue-200">
                <Percent size={16} /> Tax
              </a>
              <a href="#profile" className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:text-blue-700 hover:border-blue-200">
                <User size={16} /> Profile
              </a>
              <a href="#notifications" className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:text-blue-700 hover:border-blue-200">
                <Bell size={16} /> Notifications
              </a>
            </div>
          </div>

          <div className="p-8 space-y-10">
            <section id="currency" className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Currency</h2>
              <p className="text-sm text-slate-500 mb-5">Set default currency for invoices, reports, and dashboards.</p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Default Currency</label>
                <select
                  name="currency"
                  value={settings.currency}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {CURRENCY_OPTIONS.map((curr) => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code} - {curr.name}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            <section id="tax" className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Tax</h2>
              <p className="text-sm text-slate-500 mb-5">Configure your default tax percentage used for billing workflows.</p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Default Tax Rate (%)</label>
                <input
                  type="number"
                  name="taxRate"
                  value={settings.taxRate}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  max="100"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </section>

            <section id="profile" className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Profile</h2>
              <p className="text-sm text-slate-500 mb-5">Manage company identity and contact details used across documents.</p>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={settings.companyName}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={settings.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={settings.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={settings.address}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      value={settings.city}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                    <input
                      type="text"
                      name="state"
                      value={settings.state}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Zip/Postal Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={settings.zipCode}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={settings.country}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section id="notifications" className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Notifications</h2>
              <p className="text-sm text-slate-500 mb-5">Control in-app alerts, email updates, and digest behavior.</p>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ['in_app_enabled', 'Enable in-app notifications'],
                    ['email_enabled', 'Enable email notifications'],
                    ['mute_low_priority', 'Mute low priority alerts'],
                    ['invoice_enabled', 'Invoice alerts'],
                    ['project_enabled', 'Project alerts'],
                    ['expense_enabled', 'Expense alerts'],
                    ['security_enabled', 'Security alerts'],
                    ['system_enabled', 'System alerts'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                      <span className="text-sm font-medium text-slate-700">{label}</span>
                      <input
                        type="checkbox"
                        checked={!!notificationPrefs[key]}
                        onChange={() => handleNotificationToggle(key)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Digest Frequency</label>
                  <select
                    value={notificationPrefs.digest_frequency}
                    onChange={(e) => setNotificationPrefs((prev) => ({ ...prev, digest_frequency: e.target.value }))}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="off">Off</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                  <p className="mt-2 text-sm text-slate-500">
                    Daily digest creates an executive summary notification with overdue invoices, uninvoiced projects, and security issues.
                  </p>
                </div>
              </div>
            </section>
          </div>

          <div className="px-8 py-4 bg-slate-50 border-t flex justify-end space-x-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
